// Display-only helpers. These never decide balances or whether a transfer occurred.
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const MAX_BASE64_LENGTH = Math.ceil(MAX_IMAGE_BYTES / 3) * 4;
const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);

export function displayText(value, fallback = '') {
  return (typeof value === 'string' || typeof value === 'number') && String(value).trim()
    ? String(value).trim()
    : fallback;
}

export function shippingStatus(value) {
  const statuses = {
    SIAP_TAGIH: { label: 'Siap ditagih', tone: 'blue' },
    BELUM_HITUNG_ONGKIR: { label: 'Ongkir belum dihitung', tone: 'amber' },
    SIAP_KIRIM: { label: 'Siap dikirim', tone: 'blue' },
    DIBATALKAN: { label: 'Dibatalkan', tone: 'neutral' },
  };
  return Object.prototype.hasOwnProperty.call(statuses, value)
    ? statuses[value]
    : { label: displayText(value, 'Status belum tersedia'), tone: 'neutral' };
}

export function refundStatus(value) {
  const statuses = {
    SELESAI: { label: 'Selesai', tone: 'green' },
    MENUNGGU_KEPUTUSAN: { label: 'Menunggu keputusan', tone: 'amber' },
    MENUNGGU_VERIFIKASI_DP: { label: 'Menunggu verifikasi DP', tone: 'amber' },
    SIAP_PROSES: { label: 'Siap diproses', tone: 'blue' },
    DIBATALKAN: { label: 'Dibatalkan', tone: 'neutral' },
  };
  return Object.prototype.hasOwnProperty.call(statuses, value)
    ? statuses[value]
    : { label: displayText(value, 'Status belum tersedia'), tone: 'neutral' };
}

export function formatRupiah(value) {
  const validNumber = typeof value === 'number' && Number.isFinite(value);
  const validString = typeof value === 'string' && /^-?\d+(?:\.\d+)?$/.test(value.trim());
  if (!validNumber && !validString) return 'Menunggu konfirmasi nominal';
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Menunggu konfirmasi nominal';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency', currency: 'IDR', maximumFractionDigits: 0,
  }).format(number);
}

export function formatTrackingDate(value) {
  if (typeof value !== 'string') return 'Belum tercatat';
  const text = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2}))?$/.test(text)) {
    return 'Belum tercatat';
  }
  const day = text.slice(0, 10);
  const calendarDate = new Date(`${day}T12:00:00Z`);
  if (!Number.isFinite(calendarDate.getTime()) || calendarDate.toISOString().slice(0, 10) !== day) {
    return 'Belum tercatat';
  }
  const date = text.length === 10 ? calendarDate : new Date(text);
  if (!Number.isFinite(date.getTime())) return 'Belum tercatat';
  return new Intl.DateTimeFormat('id-ID', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Jakarta',
  }).format(date);
}

export function maskPhone(value) {
  const digits = displayText(value).replace(/\D/g, '');
  if (!digits) return 'Nomor telepon belum tersedia';
  return digits.length > 4 ? `•••• ${digits.slice(-4)}` : '••••';
}

export function recipientAddress(recipient) {
  if (!recipient || typeof recipient !== 'object') return 'Alamat belum tersedia';
  return [recipient.address, recipient.district, recipient.city]
    .map((part) => displayText(part)).filter(Boolean).join(', ') || 'Alamat belum tersedia';
}

export function hasAssetId(value) {
  return (typeof value === 'string' && value.trim().length > 0) ||
    (typeof value === 'number' && Number.isFinite(value));
}

export function safeImageDataUri(asset) {
  if (!asset || typeof asset.mime_type !== 'string' || typeof asset.data_base64 !== 'string') {
    throw new Error('INVALID_IMAGE_DATA');
  }
  const mime = asset.mime_type.trim().toLowerCase();
  if (!IMAGE_TYPES.has(mime)) throw new Error('UNSUPPORTED_IMAGE_TYPE');
  // Check size before allocating a normalized copy or decoding any bytes.
  if (!asset.data_base64.length || asset.data_base64.length > MAX_BASE64_LENGTH) {
    throw new Error('IMAGE_TOO_LARGE_OR_EMPTY');
  }
  const base64 = asset.data_base64.replace(/[\r\n]/g, '');
  if (!base64 || base64.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(base64)) {
    throw new Error('INVALID_IMAGE_DATA');
  }
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
  if (base64.length * 3 / 4 - padding > MAX_IMAGE_BYTES) throw new Error('IMAGE_TOO_LARGE');
  // Raster signatures prevent mislabeled SVG/HTML from entering the image sink.
  const header = atob(base64.slice(0, 32));
  const matches = {
    'image/png': header.startsWith('\x89PNG\r\n\x1a\n'),
    'image/jpeg': header.startsWith('\xff\xd8\xff'),
    'image/gif': header.startsWith('GIF87a') || header.startsWith('GIF89a'),
    'image/webp': header.startsWith('RIFF') && header.slice(8, 12) === 'WEBP',
  };
  if (!matches[mime]) throw new Error('INVALID_IMAGE_SIGNATURE');
  return `data:${mime};base64,${base64}`;
}
